import React, { useEffect, useState } from "react";
import hljs from "highlight.js/lib/core";
import yaml from "highlight.js/lib/languages/yaml.js";
import {
  ApiConnectionType,
  useLazyGetProjectHttpInfoQuery,
} from "../../store/slices/settingsSlice";
import MyModal from "../MyModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faDownload } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { pageProps } from "../../helpers/pageProps";
import { parse, stringify } from "yaml";
import "highlight.js/styles/github.css";

hljs.registerLanguage("yaml", yaml);

export default function LogicOpenAPI({ api }: { api: ApiConnectionType }) {
  const [getHttpInfo] = useLazyGetProjectHttpInfoQuery();
  const [projects, setProjects] = useState<any[]>();

  useEffect(() => {
    const loadHttpInfo = async () => {
      const p: any[] = [];
      if (api?.Triggers) {
        for (const trigger of api.Triggers) {
          const res = await getHttpInfo({ uuid: trigger.Project.UUID });
          if (res && "data" in res) {
            p.push(res.data);
          }
        }
      }
      setProjects(p);
    };

    loadHttpInfo();
  }, [api?.Triggers]);

  const openAPI = {
    openapi: "3.0.3",
    info: {
      title: api.Label || "Boomerang Logic API",
      description: api.Description || "API for Boomerang Logic",
      version: api.UpdatedAt || api.CreatedAt || "1.0.0",
    },
    servers: [
      {
        url: `${pageProps.inboundApiBase}/${api.Uuid}`,
      },
    ],
    paths: projects?.reduce(
      (acc: any, trigger) => ({
        ...acc,
        ["/" + trigger.Reference]: {
          post: {
            summary: trigger.Label,
            description: trigger.Description,
            parameters: [
              {
                in: "header",
                name: "X-Api-Token",
                description: "Your API token for authentication",
                schema: {
                  type: "string",
                  enum: [api.Token],
                },
                required: true,
              },
              {
                in: "header",
                name: "X-Idempotency-Key",
                description:
                  "The Idempotency Key gives you control over unintended duplication of requests. Provide a unique string, and we will check whether the same request has been made in the past (within the last 3 months) or is currently ongoing. If a previous request exists, we will return the original response.",
                schema: {
                  type: "string",
                },
                required: true,
              },
            ],
            requestBody: {
              required: true,
              content: {
                "application/json": {
                  schema: {
                    type: "object",
                    properties: Object.entries(trigger.Example).reduce(
                      (props: any, [key, value]) => {
                        props[key] = { type: "array", example: value };
                        return props;
                      },
                      {}
                    ),
                  },
                },
              },
            },
            responses: {
              "200": {
                description: `Workflow "${trigger.Label}" triggered successfully`,
                content: {
                  "application/json": {
                    schema: {
                      type: "object",
                      properties: {
                        message: {
                          type: "string",
                          example: "API connection authenticated",
                        },
                        request_id: {
                          type: "string",
                          example: "1762261400-HvimMecu9GJMJpxbiFDLvg",
                        },
                        data: {
                          type: "array",
                          items: {
                            // Add properties for the data object here
                            type: "object",
                            properties: {
                              api_call_type: {
                                type: "string",
                                example: "component",
                              },
                              call_uuid: {
                                type: "string",
                                example: "1761572194-iFBczHvNEoueSwJGt6s6ph",
                              },
                              process_id: {
                                type: "string",
                                example: "1762261400-v-JDPW-H",
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              "400": {
                description: "Bad request",
              },
              "403": {
                description: "Access denied",
                content: {
                  "application/json": {
                    schema: {
                      type: "object",
                      properties: {
                        message: {
                          type: "string",
                          example:
                            "access denied: api connection token is invalid",
                        },
                        request_id: {
                          type: "string",
                          example: "1762262576-sczYkEWAM7ofp9BUUVqcV",
                        },
                      },
                    },
                  },
                },
              },
              "404": {
                description: "Not found",
                content: {
                  "application/json": {
                    schema: {
                      type: "object",
                      properties: {
                        message: {
                          type: "string",
                          example: "not found: api connection not found",
                        },
                        request_id: {
                          type: "string",
                          example: "1762262576-sczYkEWAM7ofp9BUUVqcV",
                        },
                      },
                    },
                  },
                },
              },
              "500": {
                description: "Internal server error",
              },
            },
          },
        },
      }),
      {}
    ),
  };

  const openApiYml = stringify(openAPI);
  return (
    <MyModal
      heading="Boomerang Messaging Logic API"
      label="View OpenAPI Spec"
      buttonClassName="btn btn-secondary btn-sm px-3 fw-bold"
      size="lg"
    >
      <div className="bg-secondary-subtle p-3 rounded-3">
        <div className="text-end">
          <button
            className="btn btn-outline-secondary border-0"
            onClick={async () => {
              const res = await navigator.clipboard.writeText(
                JSON.stringify(openAPI, null, 2)
              );
              toast.success("Copied to clipboard");
            }}
          >
            <FontAwesomeIcon icon={faCopy} /> JSON
          </button>
          <button
            className="btn btn-outline-secondary border-0"
            onClick={async () => {
              const res = await navigator.clipboard.writeText(openApiYml);
              toast.success("Copied to clipboard");
            }}
          >
            <FontAwesomeIcon icon={faCopy} /> YAML
          </button>
          <button
            className="btn btn-outline-secondary border-0"
            onClick={async () => {
              var dataStr =
                "data:text/json;charset=utf-8," +
                encodeURIComponent(openApiYml);
              var downloadAnchorNode = document.createElement("a");
              downloadAnchorNode.setAttribute("href", dataStr);
              downloadAnchorNode.setAttribute(
                "download",
                `${api.Label || "Boomerang Logic API"}.yaml`
              );
              document.body.appendChild(downloadAnchorNode); // required for firefox
              downloadAnchorNode.click();
              downloadAnchorNode.remove();
            }}
          >
            <FontAwesomeIcon icon={faDownload} /> YAML
          </button>
          <button
            className="btn btn-outline-secondary border-0"
            onClick={async () => {
              var dataStr =
                "data:text/json;charset=utf-8," +
                encodeURIComponent(JSON.stringify(openAPI, null, 2));
              var downloadAnchorNode = document.createElement("a");
              downloadAnchorNode.setAttribute("href", dataStr);
              downloadAnchorNode.setAttribute(
                "download",
                `${api.Label || "Boomerang Logic API"}.json`
              );
              document.body.appendChild(downloadAnchorNode); // required for firefox
              downloadAnchorNode.click();
              downloadAnchorNode.remove();
            }}
          >
            <FontAwesomeIcon icon={faDownload} /> JSON
          </button>
        </div>
        <pre>
          <code
            dangerouslySetInnerHTML={{
              __html: hljs.highlight(openApiYml, { language: "yaml" }).value,
            }}
          />
        </pre>
      </div>
    </MyModal>
  );
}
